import DS from 'ember-data';
import Route from 'cio/routes/base';
import moment from 'moment-timezone';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';

import NewController from 'cio/pods/people/new/controller';
import CustomerModel from 'cio/models/customer';
import EnvironmentService from 'cio/services/environment';
import { IdentifierKey } from 'cio/utils/identifiers';
import Transition from '@ember/routing/-private/transition';
import ConfirmChangesService from 'cio/services/confirm-changes';
import isDestroyed from 'cio/utils/is-destroyed';
import { redirectIfNoPermission } from 'cio/utils/route-access';

export default class NewRoute extends Route {
  @service environment!: EnvironmentService;
  @service store!: DS.Store;
  @service confirmChanges!: ConfirmChangesService;

  crumb() {
    return {
      name: 'Add a Person',
      path: 'people.new',
    };
  }

  model() {
    let identifiers = this.environment.current.enabledIdentifiers.reduce<Record<IdentifierKey, string>>(
      (acc, identifier) => ({ ...acc, [identifier]: '' }),
      {} as Record<IdentifierKey, string>,
    );

    // Add backwards compatibility with ID workspaces and show email even though
    // it's not an identifier
    if (this.environment.current.canIdentifyById) {
      identifiers[IdentifierKey.Email] = '';
    }

    return this.store.createRecord('customer', {
      attrs: { ...identifiers, created_at: moment().unix() },
    });
  }

  async beforeModel(transition: Transition) {
    await super.beforeModel(transition);

    return redirectIfNoPermission(this, 'journeys/people/create', transition);
  }

  deactivate() {
    super.deactivate();
    this.rollbackModelFor();
  }

  setupController(controller: NewController, model: CustomerModel, transition: Transition) {
    super.setupController(controller, model, transition);

    controller.prepareAttributesProxy();
    this.confirmChanges.register(controller, this);
  }

  @action
  willTransition() {
    let customerModel = this.modelFor(this.routeName) as CustomerModel;

    // The customer record created on this page is temporary, since the back end created new
    // customers via the identify queue. The actual record will be queried later, so it's safe to
    // unload this customer record every time.
    if (!isDestroyed(customerModel)) {
      customerModel.unloadRecord();
    }
  }
}
